import { computed, effect, signal } from '@preact/signals';
import type { Cache, CachedEdge, CachedMembership } from './cache.js';
import {
  idOf,
  type AlbumItem,
  type Entity,
  type FeedItem,
  type Folder,
  type Group,
  type Me,
  type Member,
  type Reactor,
  type Revision,
  type Share,
  type ShareEvent,
} from './api.js';
import { isSearching, search } from './search.js';
import {
  DEFAULT_THRESHOLD,
  MAX_THRESHOLD,
  duplicateGroups,
  sortByDuplicates,
  type Fingerprinted,
} from './visual-duplicates.js';

/**
 * L'état de l'application, en **signaux**.
 *
 * Ce qui change par rapport à l'étape 3 : la page n'est plus redessinée en entier
 * après chaque mutation. Ce choix-là était le bon pour deux écrans, et il casse
 * dès qu'un champ de texte a le focus, qu'un défilement doit survivre ou qu'un
 * curseur d'édition existe — chacun était détruit à chaque frappe. Un éditeur de
 * notes est exactement ce cas, et il arrive en 4.3.
 *
 * Le miroir reste **la seule source** : `items` en est une projection calculée,
 * jamais une seconde liste tenue à jour en parallèle. Deux listes finissent par
 * diverger, et rien ne le signale.
 */

export const ROOT = '/';

export type View =
  | { readonly kind: 'folder'; readonly folder: string }
  | { readonly kind: 'files' }
  | { readonly kind: 'collection'; readonly aid: string }
  | { readonly kind: 'shared' }
  | { readonly kind: 'group'; readonly gid: string; readonly tab: GroupTab }
  | { readonly kind: 'me' }
  /**
   * **La console d'administration.** Elle vit à côté de *Moi*, hors du rail, pour
   * la même raison : ce n'est pas une destination de travail. Et elle n'est pas
   * une destination *de plus* — c'est un écran qu'un utilisateur ordinaire ne
   * verra jamais, la porte n'apparaissant que si `me.isAdmin`.
   */
  | { readonly kind: 'admin' }
  /**
   * **La corbeille.** Elle est atteinte depuis *Moi*, hors du rail, et pour la
   * même raison que l'administration : ce n'est pas un lieu où l'on travaille,
   * c'est un endroit où l'on va **une fois**, quand on regrette un geste. Lui
   * donner une destination l'aurait mise sur le chemin de tous les jours.
   */
  | { readonly kind: 'trash' }
  /**
   * **Les nouveautés.** Comme la corbeille et l'administration : un endroit où
   * l'on va *une fois*, quand on se demande ce qui a changé — jamais un lieu de
   * travail, donc pas une destination du rail.
   */
  | { readonly kind: 'news' };

/**
 * Les trois faces d'un groupe. **L'audience d'abord** : un groupe *est* des gens,
 * et ce qui y circule vient après. L'ordre des onglets dit lequel des deux on
 * choisit en premier — et c'est bien l'audience qui dure, les collections passent.
 */
export type GroupTab = 'collections' | 'feed' | 'members';

/**
 * **Deux destinations**, définies par la question qu'on se pose — et *Moi* n'en
 * est pas une : c'est un écran de réglages, atteint par l'avatar en tête. Le
 * mettre dans le rail lui donnait le même poids qu'un lieu où l'on travaille.
 *
 * ### « Mes fichiers » a cessé d'être une destination *(2026-08-09)*
 *
 * Elle en était une tant que l'arborescence ne montrait que du texte : deux
 * écrans, deux rendus, deux façons de regarder la même chose. Depuis que le
 * dossier se peint en vignettes, il ne restait entre les deux qu'**un axe de
 * lecture** — *par dossier* ou *par date* — et un axe n'est pas un lieu. Deux
 * entrées de rail portant le même contenu obligeaient à choisir avant de savoir
 * ce qu'on cherche, et les filtres (type, note, emoji, appareil) ne vivaient que
 * d'un seul côté alors qu'ils valent des deux.
 *
 * *Mon espace* porte donc les deux axes, et le commutateur est **dans l'écran**,
 * au-dessus de la liste. Le panneau d'arborescence peut désormais rester monté
 * sur l'axe *Tout* sans mentir : cliquer un dossier ne fait plus quitter la
 * destination — c'était le piège de 4.5 —, il change d'axe à l'intérieur d'elle.
 */
export type Destination = 'space' | 'shared';

/**
 * **Une collection appartient aux partages**, pas à l'arborescence.
 *
 * C'est la correction qui fonde 4.5. Tant qu'un dossier et une collection
 * s'affichaient au même endroit, l'écran proposait deux façons de grouper sans
 * dire laquelle choisir — et l'une des deux, la collection, était en réalité un
 * geste de partage déguisé en rangement. Le dossier est un *emplacement* :
 * exclusif, privé. La collection est un *ensemble* : N-N, et la seule chose que
 * le socle sache exposer à un groupe.
 */
export const destinationOf = (v: View): Destination | undefined => {
  // Les **deux axes** de *Mon espace* : par dossier, ou tout par date.
  if (v.kind === 'folder' || v.kind === 'files') return 'space';
  if (v.kind === 'collection' || v.kind === 'group' || v.kind === 'shared') return 'shared';
  // *Moi* et l'administration ne sont pas dans le rail : aucune de leurs vues
  // n'y allume donc quoi que ce soit.
  return undefined;
};

/**
 * **L'écran d'où celui-ci descend** — ce qui rend un bouton *Retour* possible.
 *
 * Il n'y en avait aucun, et le retour du navigateur n'existait pas non plus
 * (`navigation.ts`) : ouvrir un groupe ou une collection était un aller sans
 * retour, et il fallait repasser par le rail — qui ramène à la *liste*, pas au
 * niveau au-dessus. Le retour d'usage le dit deux fois : « dans mes partages
 * quand j'ai cliqué sur un groupe je n'ai pas de retour vers la liste des
 * groupes […] idem quand je suis sur un album d'un groupe ».
 *
 * C'est une **remontée**, pas un historique : elle est déterministe et ne dépend
 * pas du chemin par lequel on est arrivé. Les deux coexistent — le geste système
 * défait le dernier pas, le bouton remonte d'un cran, et il arrive qu'ils ne
 * mènent pas au même endroit, ce qui est normal et attendu partout ailleurs.
 *
 * Le parent d'une collection est **son groupe**, quand on le connaît :
 * `authGroups` est filtré au point de vue de l'appelant, donc le premier est un
 * cercle qu'on partage. Une collection sans groupe connu remonte à la liste — la
 * seule réponse vraie quand on ne sait pas.
 */
export const parentOf = (v: View): View | undefined => {
  switch (v.kind) {
    case 'group':
      return { kind: 'shared' };
    case 'collection': {
      const gid = (albums.value ?? []).find((a) => a.Aid === v.aid)?.authGroups?.[0];
      return gid ? { kind: 'group', gid, tab: 'collections' } : { kind: 'shared' };
    }
    case 'folder': {
      if (v.folder === ROOT) return undefined;
      const cut = v.folder.lastIndexOf('/');
      return { kind: 'folder', folder: cut <= 0 ? ROOT : v.folder.slice(0, cut) };
    }
    // *Moi* et l'administration sont atteints par l'avatar, d'où l'on ne revient
    // pas : elles remontent donc vers l'espace, là où l'on travaille.
    case 'me':
    case 'admin':
      return { kind: 'folder', folder: lastFolder.value };
    // La corbeille, elle, **remonte vers *Moi*** : c'est de là qu'on y entre, et
    // c'est le seul écran dont le parent ne soit pas l'espace de travail.
    case 'trash':
    // Les nouveautés remontent là aussi : on y entre par l'avatar, et *Moi* est
    // le seul écran de cette famille d'où l'on revient.
    case 'news':
      return { kind: 'me' };
    default:
      return undefined;
  }
};

/** L'axe de lecture de *Mon espace* — le seul choix que le commutateur porte. */
export type SpaceAxis = 'folder' | 'all';

export const axisOf = (v: View): SpaceAxis | undefined =>
  v.kind === 'folder' ? 'folder' : v.kind === 'files' ? 'all' : undefined;

/**
 * **Où l'on en était, d'une session à l'autre** *(2026-08-09)*.
 *
 * Le dossier ouvert et l'axe de lecture repartaient de zéro à chaque
 * rafraîchissement : on rouvrait le site à la racine, quel que soit l'endroit
 * où l'on travaillait la minute d'avant. C'est le retour d'usage le plus net de
 * la passe — et ce n'est pas un confort, c'est ce qui distingue un outil d'une
 * page : un explorateur de fichiers qui remonte à la racine à chaque ouverture
 * demande de refaire à la main un chemin qu'il connaît.
 *
 * C'est une **préférence d'appareil**, comme la densité et la présentation :
 * elle vit dans `localStorage`, à côté d'elles, et non dans le compte — deux
 * appareils ne regardent pas le même dossier au même moment.
 *
 * Ce qui est gardé est **l'endroit, pas la destination** : rouvrir sur *Mes
 * partages* parce qu'on y était en fermant l'onglet cacherait son propre espace
 * derrière un écran qu'on n'a pas demandé. On rouvre donc toujours dans *Mon
 * espace*, là où on l'avait laissé.
 */
const SPACE_KEY = 'synkara.space';

interface SpaceMemo {
  readonly axis: SpaceAxis;
  readonly folder: string;
}

function readSpaceMemo(): SpaceMemo {
  try {
    const raw = localStorage.getItem(SPACE_KEY);
    if (!raw) return { axis: 'folder', folder: ROOT };
    const parsed = JSON.parse(raw) as Partial<SpaceMemo>;
    return {
      axis: parsed.axis === 'all' ? 'all' : 'folder',
      // Un chemin doit commencer par `/` : une valeur écrite par une version
      // antérieure — ou trafiquée — ne doit pas produire une vue impossible.
      folder: typeof parsed.folder === 'string' && parsed.folder.startsWith('/') ? parsed.folder : ROOT,
    };
  } catch {
    // Un mémo illisible n'est pas une panne : on repart de la racine, qui est
    // le défaut d'origine.
    return { axis: 'folder', folder: ROOT };
  }
}

const memo = readSpaceMemo();

/**
 * **Le dernier dossier regardé**, pour que revenir sur l'axe *Dossiers* revienne
 * là où l'on était. Sans lui, le commutateur ramènerait à la racine — c'est-à-dire
 * qu'un aller-retour entre deux façons de regarder ferait perdre sa place.
 */
export const lastFolder = signal<string>(memo.folder);

export const me = signal<Me | undefined>(undefined);

/**
 * **La fenêtre de corbeille, telle que le serveur la sert** — jamais un 30 écrit
 * ici *(2026-09-12)*.
 *
 * Trois phrases d'écran annonçaient « 30 jours » en dur, et deux autres disaient
 * de la même suppression qu'elle était « irrécupérable ». Un plafond recopié dans
 * un client finit toujours par contredire le serveur : c'est la règle qui a fait
 * servir `limits` et `quotas`, et la rétention n'y échappe pas.
 *
 * `undefined` quand `/me` n'a pas encore répondu, et **c'est voulu** : l'appelant
 * choisit alors une phrase **sans nombre** plutôt que d'en inventer un. Un repli à
 * 30 aurait recréé la seconde source de vérité qu'on vient de retirer.
 */
export const trashWindow = computed<number | undefined>(() => {
  const days = me.value?.retention?.['trashDays'];
  return typeof days === 'number' && days > 0 ? days : undefined;
});
export const entities = signal<readonly Entity[]>([]);
export const folders = signal<readonly Folder[]>([]);
export const groups = signal<readonly Group[] | undefined>(undefined);
export const view = signal<View>(
  memo.axis === 'all' ? { kind: 'files' } : { kind: 'folder', folder: memo.folder },
);

/**
 * L'écriture du mémo suit la vue, **sans que personne ait à y penser**.
 *
 * Un `effect` plutôt qu'un appel dans `openView` : la vue change aussi par la
 * création d'un dossier et par la disparition de celui qu'on regardait, et un
 * point d'écriture unique est le seul moyen qu'aucun de ces chemins ne l'oublie.
 */
effect(() => {
  const v = view.value;
  const axis = axisOf(v);
  if (axis === undefined) return;
  try {
    localStorage.setItem(
      SPACE_KEY,
      JSON.stringify({ axis, folder: v.kind === 'folder' ? v.folder : lastFolder.peek() }),
    );
  } catch {
    // Un stockage plein ou refusé ne doit pas empêcher de naviguer.
  }
});
/**
 * Le formulaire ouvert par le « + ». **Un par écran, jamais un par type** : c'est
 * la règle de 4.5 bis, et *Mes partages* n'y fait pas exception depuis 4.6 — un
 * groupe, une collection et une invitation s'ouvrent par le même bouton que le
 * dossier et la note, au même endroit sous la barre.
 */
export type FormKind = 'folder' | 'note' | 'group' | 'collection' | 'invite';
export const form = signal<FormKind | undefined>(undefined);

export const busy = signal<string | undefined>(undefined);
export const error = signal<string | undefined>(undefined);

/**
 * **Les octets d'un envoi en cours** — la barre que le retour d'usage réclamait
 * *(2026-09-12)* : « on voit la progression des éléments (ex : 2/10) mais on ne voit
 * pas une barre de progression des octets uploadés ».
 *
 * Séparée de `busy` pour une raison de fond : `busy` est un **texte**, et il pilote
 * aussi la désactivation des boutons. La progression, elle, est un **nombre** qui
 * change cent fois par fichier. Les mêler aurait fait dépendre l'état
 * d'activation de l'écran du débit de la liaison montante.
 *
 * `undefined` quand aucun octet n'est en vol. Le compte est **par fichier** et non
 * pour le lot : c'est ce que le navigateur sait rapporter, et c'est ce qui bouge
 * quand une seule photo de 38 Mio remonte lentement.
 */
export const uploadProgress = signal<{ sent: number; total: number } | undefined>(undefined);

/**
 * **La question des doublons exacts** *(K2, DB2)* — posée entre le hachage et
 * l'envoi, quand la sélection en contient. `choose(false)` envoie les autres,
 * `choose(true)` envoie tout. `undefined` hors de cet instant.
 */
export interface ImportPrompt {
  readonly duplicates: number;
  readonly inTrash: number;
  readonly choose: (force: boolean) => void;
}
export const importPrompt = signal<ImportPrompt | undefined>(undefined);

/**
 * **Les descriptions demandées et pas encore revenues** *(R6)* — par `fid`. La fiche
 * y lit « description demandée » au lieu de rester immobile après « Régénérer » ;
 * l'entrée tombe dès que la synchronisation rapporte la nouvelle description, ou
 * quand le suivi renonce.
 */
export const describing = signal<ReadonlySet<string>>(new Set());

/**
 * L'état du lien. Il ne pilote rien en 4.0 — l'outbox est en 4.7 — mais il se
 * **dit** : une action qui échoue sans qu'on sache qu'on est hors ligne passe
 * pour une panne du service.
 */
export const online = signal<boolean>(navigator.onLine);
window.addEventListener('online', () => (online.value = true));
window.addEventListener('offline', () => (online.value = false));

/**
 * Ce qui n'est pas rendu ne mérite pas d'être un signal : le cache et le curseur
 * ne s'affichent nulle part, et en faire des signaux ferait redessiner l'écran à
 * chaque page de synchronisation drainée.
 */
export const session: { cache?: Cache; cursor?: string } = {};

/**
 * **Les filtres de la photothèque** (4.4), et ils sont **clients**.
 *
 * `vision.md` §6.2 les écarte volontairement du serveur : `Camera`, auteur et emoji
 * sont *renvoyés*, jamais interrogés. Aucun index ne les porte, et en fabriquer
 * demanderait un GSI par critère pour un corpus qui tient dans le miroir. La limite
 * est donc la même que celle de la recherche (C6) — **ils portent sur ce qui est
 * synchronisé** — et l'écran doit le dire plutôt que de laisser croire à un vide.
 */
export interface GalleryFilter {
  /**
   * `NOTE`, `DOC` ou `MEDIA`. Depuis que « Mes fichiers » les porte tous, c'est
   * le filtre le plus demandé — et il remplace les cinq onglets par type de la V1
   * sans les faire revenir : un filtre se cumule, un onglet exclut.
   */
  readonly type?: string;
  /** `IMG` ou `VID` — le `Kind` d'un `MEDIA`. */
  readonly kind?: string;
  /** Note **minimale** : « au moins 3 étoiles », jamais « exactement 3 ». */
  readonly minScore?: number;
  readonly emoji?: string;
  readonly camera?: string;
  /**
   * **Le partage** *(R3, 2026-09-12)* — un `aid`, ou `null` pour *ce qui ne
   * circule pas*.
   *
   * Le retour d'usage : « lorsqu'on est dans un dossier il manque un filtre sur
   * les partages (pour lister les éléments partagés sur un partage spécifique ou
   * dans aucun partage) ». Les deux moitiés de la phrase sont deux critères, et
   * c'est pourquoi le champ a **trois** états et non deux : `undefined` = « je
   * n'ai rien demandé », un `aid` = « ceux-là », `null` = « aucun ». Confondre
   * les deux derniers rendrait la question la plus utile — *qu'est-ce que je n'ai
   * pas encore partagé ?* — impossible à poser.
   *
   * Il lit `albums`, que le serveur sert au propriétaire depuis l'étape 1
   * (`api.ts`). Il est donc, comme les autres, **entièrement client**.
   */
  readonly album?: string | null;
  /**
   * **Le propriétaire** *(R3, 2026-09-12)* — un `uid`, dans un partage seulement.
   *
   * « Sur un album partagé il manque un filtre par propriétaire pour isoler
   * facilement ses éléments ou ceux d'un autre utilisateur ». C'est le pendant du
   * précédent, et il vit de l'autre côté : dans un dossier, tout est à soi et la
   * question ne se pose pas ; dans un partage collectif, elle est la première.
   */
  readonly owner?: string;
  /**
   * **Le mode doublons** *(K4)* — **un interrupteur, pas un réglage permanent**
   * *(retour d'usage du 2026-09-13 : « le dédoublonnage visuel est permanent, il
   * faudrait pouvoir activer/désactiver le filtre »)*. Désactivé, rien n'est
   * calculé ni affiché. Activé, la grille ne montre que les photos qui ont au moins
   * un voisin au seuil réglé, **regroupées**, avec leur badge.
   *
   * Comme `album`, il ne vaut que dans son propre espace : les empreintes ne sont
   * servies qu'au propriétaire et ne voyagent pas sur les arêtes d'un partage.
   */
  readonly duplicates?: true;
}

/**
 * **Tous les filtres ne valent pas partout, et c'est la seule exception à la règle
 * du 2026-08-14** *(R3, 2026-09-12)*.
 *
 * Type, note, appareil et emoji portent sur l'**objet** : ils ont donc le même sens
 * dans un dossier, sur l'axe *Tout* et dans un partage, et les réserver à un écran
 * avait été le défaut corrigé ce jour-là. Les deux critères de R3, eux, portent sur
 * ce que l'écran **peut savoir** :
 *
 * - `album` lit `albums`, que seule une **entité** porte. Le contenu d'un partage
 *   vient d'arêtes, où la projection n'a jamais écrit ce champ (D28, I-27) :
 *   l'appliquer là viderait la grille en prétendant filtrer ;
 * - `owner` n'a de sens que là où il y a plusieurs propriétaires. Dans son propre
 *   espace, il n'y en a qu'un — le filtre y répondrait « tout » ou « rien ».
 *
 * D'où une fonction **pure**, et non deux `if` enfouis dans un composant : c'est
 * elle qui décide ce que la grille applique **et** ce que la pastille compte, et
 * deux endroits qui répondent différemment à « ce filtre est-il actif ? » est
 * exactement la divergence qu'on ne voit jamais venir.
 */
export function applicableFilter(f: GalleryFilter, kind: View['kind']): GalleryFilter {
  const out = { ...f };
  if (kind !== 'folder' && kind !== 'files') {
    delete (out as { album?: unknown }).album;
  }
  // **Les doublons valent aussi dans un partage** *(DB12, 2026-09-20)* — et c'est
  // là qu'ils comptent le plus : plusieurs personnes y versent la même photo. Ils
  // n'ont de sens nulle part ailleurs : le fil d'un groupe, *Moi*, la corbeille et
  // l'administration ne peignent pas de grille comparable.
  if (kind !== 'folder' && kind !== 'files' && kind !== 'collection') {
    delete (out as { duplicates?: unknown }).duplicates;
  }
  if (kind !== 'collection') delete (out as { owner?: unknown }).owner;
  return out;
}

/**
 * **Le mode doublons est-il offert sur cet écran ?**
 *
 * Écrit **le 2026-09-20, après un défaut** : DB12 avait ouvert le filtre au
 * partage dans `applicableFilter`, mais la case du tiroir gardait sa propre liste
 * d'écrans — la fonction disait oui, l'écran n'offrait rien, et aucune mesure ne
 * pouvait le voir puisqu'elles portaient toutes sur la fonction. **L'écran pose
 * donc la question à la règle**, au lieu de la redire.
 */
export const duplicatesAvailable = (kind: View['kind']): boolean =>
  applicableFilter({ duplicates: true }, kind).duplicates === true;

export type GallerySort = 'kind' | 'date-desc' | 'date-asc' | 'name' | 'size-desc';

/**
 * **Le seuil de ressemblance** *(K4, DB10)* — distance de Hamming maximale entre deux
 * empreintes visuelles pour les dire doublons. 2 par défaut : les doublons vrais,
 * copies WhatsApp comprises. Jusqu'à 20 : les photos proches d'un même lieu. C'est
 * une préférence d'appareil, gardée comme la présentation de la grille.
 */
const THRESHOLD_KEY = 'synkara.duplicateThreshold';

export const duplicateThreshold = signal<number>(
  ((): number => {
    try {
      const raw = Number(localStorage.getItem(THRESHOLD_KEY));
      return Number.isInteger(raw) && raw >= 0 && raw <= MAX_THRESHOLD && localStorage.getItem(THRESHOLD_KEY) !== null
        ? raw
        : DEFAULT_THRESHOLD;
    } catch {
      return DEFAULT_THRESHOLD;
    }
  })(),
);

export function setDuplicateThreshold(next: number): void {
  duplicateThreshold.value = Math.max(0, Math.min(MAX_THRESHOLD, Math.round(next)));
  try {
    localStorage.setItem(THRESHOLD_KEY, String(duplicateThreshold.value));
  } catch {
    // Un navigateur qui refuse le stockage garde le réglage le temps de la page.
  }
}

/**
 * **Les filtres valent des deux côtés, le tri non.**
 *
 * Un filtre répond à *lequel ?* et la question ne dépend pas de l'axe : chercher
 * ses vidéos notées 4 est la même intention dans un dossier et dans la vue par
 * date. Il y a donc **un seul** jeu de filtres, et c'est ce que demandait le
 * retour d'usage.
 *
 * Le tri, lui, *est* l'axe : un dossier se lit par type puis par nom — c'est ce
 * qui met les dossiers d'un même genre côte à côte —, une vue *Tout* par date. Un
 * tri unique aurait obligé l'un des deux à repartir du mauvais ordre à chaque
 * bascule.
 */
export const galleryFilter = signal<GalleryFilter>({});

/**
 * **La recherche par le contenu** *(R7, 2026-09-13)* — ce qu'on a tapé.
 *
 * Elle n'est **pas** un critère de `GalleryFilter`, et la différence est de
 * comportement : un filtre **retient**, une recherche **classe**. « plage mer »
 * met en tête ce qui trouve les deux mots, puis ce qui en trouve un — et l'ordre
 * de l'écran cède alors la place à la pertinence. La ranger avec les filtres
 * aurait fait effacer la saisie par « Effacer les filtres », ou fait compter un
 * mot tapé comme un critère dans la pastille du tiroir.
 *
 * Elle vaut **là où l'on regarde**, comme les filtres : un dossier, l'axe *Tout*,
 * une collection — et une collection partagée se cherche comme les autres, les
 * mots-clés et la description voyageant sur les arêtes (D28).
 */
export const searchQuery = signal<string>('');

/** Vrai dès que la requête porte un mot cherchable : l'écran passe en résultats. */
export const searching = computed<boolean>(() => isSearching(searchQuery.value));

/**
 * Le nom de chaque partage, par `aid` — ce que la recherche lit pour trouver une
 * photo par la collection où elle est rangée. Au propriétaire seul : `albums`
 * n'existe que sur l'entité (I-27).
 */
const albumNames = computed<ReadonlyMap<string, string>>(
  () => new Map((albums.value ?? []).filter((a) => a.Aid).map((a) => [String(a.Aid), a.Name])),
);
const folderSort = signal<GallerySort>('kind');
const filesSort = signal<GallerySort>('date-desc');

export const gallerySort = computed<GallerySort>(() =>
  view.value.kind === 'folder' ? folderSort.value : filesSort.value,
);

export function setGallerySort(next: GallerySort): void {
  if (view.value.kind === 'folder') folderSort.value = next;
  else filesSort.value = next;
}

/**
 * **La densité de la grille** — combien de vignettes en largeur.
 *
 * Une seule taille de tuile ne sert pas deux usages : on trie cinq cents photos
 * de vacances en les survolant, et on en choisit une en la regardant. Sur un
 * téléphone la tuile de 160 px n'en laissait tenir que deux par rangée, ce qui
 * fait défiler un gros dossier pendant une minute.
 *
 * Les trois pas sont donc **1 / 3 / 5 en largeur sur un écran étroit**, et sur un
 * grand écran la même intention exprimée en *largeur minimale de tuile* — le
 * nombre de colonnes s'y adapte alors à la place réelle, qui change avec
 * l'inspecteur et avec la fenêtre. Fixer un nombre sur un écran large donnerait
 * des vignettes de 600 px de côté.
 *
 * C'est une **préférence d'appareil** : elle se garde, comme le choix
 * lignes/vignettes, parce que personne ne veut la redire à chaque dossier.
 */
export type GridDensity = 'large' | 'medium' | 'small';

const DENSITY_KEY = 'synkara.density';

/** Le nombre de colonnes sur un écran étroit — exactement ce qui a été demandé. */
export const NARROW_COLUMNS: Readonly<Record<GridDensity, number>> = {
  large: 1,
  medium: 3,
  small: 5,
};

/** La largeur minimale d'une tuile sur un écran large, en pixels CSS. */
export const WIDE_MIN_TILE: Readonly<Record<GridDensity, number>> = {
  large: 320,
  medium: 160,
  small: 104,
};

export const gridDensity = signal<GridDensity>(
  ((): GridDensity => {
    const raw = localStorage.getItem(DENSITY_KEY);
    return raw === 'large' || raw === 'small' ? raw : 'medium';
  })(),
);

export function setGridDensity(next: GridDensity): void {
  gridDensity.value = next;
  localStorage.setItem(DENSITY_KEY, next);
}

/** Les collections, et le contenu de celle qu'on regarde. */
export const albums = signal<readonly Entity[] | undefined>(undefined);

/**
 * **La corbeille**, et pourquoi elle a son propre signal plutôt qu'un filtre sur
 * `entities`.
 *
 * Un objet en corbeille a **quitté** les index de listing : il n'est ni dans le
 * miroir local, ni dans `entities`, et il n'y a donc rien à filtrer. L'y verser
 * serait pire qu'inutile — la galerie, les dossiers et les partages lisent tous
 * `entities`, et un objet supprimé y réapparaîtrait partout.
 *
 * `undefined` = « on n'a pas encore demandé », `[]` = « la corbeille est vide ».
 * Les confondre ferait afficher « rien à restaurer » pendant le chargement.
 */
export const trash = signal<readonly TrashItem[] | undefined>(undefined);

/** Une entité de corbeille porte **sa date de purge**, servie et jamais recalculée. */
export type TrashItem = Entity & { readonly purgeAt: string | null };
/**
 * Le contenu du partage ouvert. Ce sont des **arêtes** hydratées (`AlbumItem`), pas
 * des entités : `ownerAlias` y est, `albums` et `Folder` n'y seront jamais.
 */
export const albumItems = signal<readonly AlbumItem[]>([]);

/**
 * **Les partages où l'on peut verser** — et c'est la seule liste qu'un sélecteur
 * doive montrer *(2026-08-16)*.
 *
 * `GET /albums` rend l'union des siens et de ceux qu'on lui partage ; la route
 * d'ajout, elle, n'accepte un tiers que sous `ContribPolicy = members`. Un
 * partage reçu et **fermé** figurait donc dans le sélecteur comme les autres, et
 * le choix partait pour rendre `403`.
 *
 * Le filtre est la garde du serveur, écrite dans les mêmes termes — et c'est une
 * fonction et non une expression enfouie dans un composant, pour qu'elle se
 * mesure sans allumer un navigateur.
 *
 * **`exclude` — celui qu'on regarde** *(R3, 2026-09-12)*. Depuis que la barre de
 * sélection propose « ajouter » aussi dans un partage, le sélecteur peut s'ouvrir
 * par-dessus l'endroit d'où viennent les éléments. Se verser dans soi-même rendrait
 * `already` sur toute la ligne : un geste sans effet, proposé en toutes lettres,
 * c'est-à-dire exactement ce que le paragraphe ci-dessus interdit. Le cas est passé
 * en argument plutôt que lu dans `view` ici : cette fonction ne connaît pas
 * d'écran, et c'est ce qui la rend mesurable.
 */
export function contributableAlbums(
  list: readonly Entity[],
  uid: string | undefined,
  exclude?: string | undefined,
): readonly Entity[] {
  // `Owner` absent veut dire « c'est à moi » : le sérialiseur ne le pose que sur
  // ce qui est reçu.
  return list.filter(
    (a) =>
      a.Aid !== exclude &&
      (a.Owner === undefined || a.Owner === uid || a.ContribPolicy === 'members'),
  );
}

/**
 * **Les partages rangés par groupe** *(2026-08-16)*.
 *
 * Le sélecteur alignait tout sur une liste plate, nom du partage et groupe sur la
 * même ligne. Deux défauts en un : la ligne était illisible — la règle de colonne
 * ne connaissait pas `.cards .card`, si bien que les deux mots se touchaient —, et
 * la **forme** ne tenait qu'à trois partages. Le groupe est de toute façon la
 * première question, puisque verser dans un partage, c'est donner à son audience.
 *
 * Trois points que le code doit garantir et qu'une prose ne garantit pas :
 *
 * - un partage exposé à **deux** groupes apparaît **sous les deux**. Ce n'est pas
 *   une duplication : ce sont deux audiences, et c'est l'audience qu'on choisit ;
 * - un groupe **sans aucun partage** reste dans la liste. C'est là qu'on en crée
 *   un, et le masquer rendrait la création impossible là où elle sert le plus ;
 * - un partage dont l'exposition a été **révoquée** n'a plus de groupe. Il reste
 *   joignable sous `undefined`, faute de quoi un partage qu'on possède
 *   deviendrait invisible sans que rien ne le dise.
 */
export interface GroupedAlbums {
  /** `undefined` pour les partages sans exposition. */
  readonly gid: string | undefined;
  readonly name: string | undefined;
  readonly albums: readonly Entity[];
}

export function albumsByGroup(
  list: readonly Entity[],
  known: readonly { readonly gid: string; readonly name: string }[],
): readonly GroupedAlbums[] {
  const out: GroupedAlbums[] = known.map((g) => ({
    gid: g.gid,
    name: g.name,
    albums: list.filter((a) => (a.authGroups ?? []).includes(g.gid)),
  }));
  const orphans = list.filter((a) => (a.authGroups ?? []).length === 0);
  return orphans.length > 0 ? [...out, { gid: undefined, name: undefined, albums: orphans }] : out;
}

/**
 * **Les partages qui contiennent déjà toute la sélection** *(R3, 2026-09-12)*.
 *
 * Le sélecteur s'en sert pour deux choses, et la seconde est celle qui compte :
 * marquer ce qui est déjà fait, et **ne pas le refaire**. Depuis qu'il reste
 * ouvert pour verser dans plusieurs partages d'affilée, l'écran garde sous les
 * yeux une liste où l'on vient de cliquer — sans marque, on reclique.
 *
 * **« Toute la sélection », et non « au moins un »** : un partage qui contient
 * trois photos sur dix n'est pas fait, et l'annoncer fait manquer les sept
 * autres. Un `fid` que le miroir ne connaît pas — le contenu d'un partage vient
 * d'arêtes, qui ne portent pas `albums` — est **ignoré** plutôt que compté comme
 * absent : sans quoi le marquage disparaîtrait entièrement dès qu'on verse depuis
 * un partage, ce que R3 vient précisément d'ouvrir. Le prix est connu : dans ce
 * cas, la marque est une information partielle, jamais une fausse.
 */
export function albumsOfAll(
  fids: readonly string[],
  known: readonly Entity[],
): ReadonlySet<string> {
  const byFid = new Map(known.filter((e) => e.Fid).map((e) => [String(e.Fid), e]));
  const seen = fids.map((fid) => byFid.get(fid)).filter((e): e is Entity => e !== undefined);
  if (seen.length === 0) return new Set();
  return seen
    .map((e) => new Set(e.albums ?? []))
    .reduce((acc, next) => new Set([...acc].filter((aid) => next.has(aid))));
}

/**
 * **Le dernier partage servi**, et la raison pour laquelle il est vérifié plutôt
 * que cru *(2026-08-16)*.
 *
 * Le proposer d'emblée épargne deux gestes dans le cas de très loin le plus
 * fréquent — on verse plusieurs fois de suite au même endroit. Mais un souvenir
 * de navigateur survit à tout : au partage supprimé, à l'accès révoqué, et au
 * **changement de compte** sur le même poste. D'où les deux gardes : la mémoire
 * porte l'`uid` de qui l'a écrite, et l'`aid` n'est rendu que s'il figure encore
 * dans la liste où l'on peut verser.
 */
const LAST_ALBUM_KEY = 'synkara.lastAlbum';

export function rememberLastAlbum(uid: string | undefined, aid: string): void {
  if (!uid) return;
  try {
    localStorage.setItem(LAST_ALBUM_KEY, `${uid}|${aid}`);
  } catch {
    // Un stockage refusé (navigation privée, quota) ne doit pas faire échouer un
    // ajout qui, lui, a réussi.
  }
}

export function lastAlbumOf(
  uid: string | undefined,
  list: readonly Entity[],
): Entity | undefined {
  if (!uid) return undefined;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LAST_ALBUM_KEY);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;
  const cut = raw.indexOf('|');
  if (cut < 0) return undefined;
  // L'`uid` d'abord : sur un poste partagé, la mémoire du compte précédent
  // désignerait un partage que celui-ci ne voit pas — ou pire, qu'il voit.
  if (raw.slice(0, cut) !== uid) return undefined;
  const aid = raw.slice(cut + 1);
  return list.find((a) => a.Aid === aid);
}

/**
 * **Puis-je écrire cette note ?** — la question que se pose tout écran qui montre
 * le verbe « Écrire ».
 *
 * Trois réponses, dans cet ordre, et l'ordre est la règle :
 *
 * ① **Ce que le serveur a dit**, s'il l'a dit. `GET /files/{fid}` porte le droit
 *    calculé ; rien ici ne peut être plus exact que lui.
 * ② **La mienne** — une entité sans `Owner` est une entité locale, donc la mienne.
 * ③ **Le partage d'où je la regarde.** Une entité venue d'une liste ne porte pas
 *    le droit, et il n'est pas déductible d'elle : `Albums` ne sort jamais vers un
 *    tiers. Mais un partage collectif *ouvert sous les yeux* répond à la question
 *    pour ce qu'il contient — c'est le bloc d'audience, juste au-dessus, qui le dit.
 *
 * Hors de ces trois cas la réponse est **non**, et c'est délibérément prudent :
 * une note reçue ouverte depuis *Nouveautés* n'offrira pas « Écrire », même si le
 * partage qui l'a portée est collectif. Proposer un geste refusé coûte plus cher
 * que de ne pas le proposer — et le même objet, ouvert depuis le partage, l'offre.
 */
export function canWriteNote(entity: Entity): boolean {
  if (typeof entity.canWriteContent === 'boolean') return entity.canWriteContent;
  if (entity.Owner === undefined || entity.Owner === me.value?.uid) return true;

  const v = view.value;
  if (v.kind !== 'collection') return false;
  return (albums.value ?? []).find((a) => a.Aid === v.aid)?.ContribPolicy === 'members';
}

// ─────────────────────────────────────────────────────────────────────────────
// Les partages (4.6)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * **Ce qui nous est partagé, tel que le drainage l'a écrit** — arêtes et
 * appartenances, reprises du miroir depuis que D-23 est fermée.
 *
 * Une arête n'est **pas** une entité, et les fondre dans `entities` aurait été la
 * facilité qui coûte cher : elle porte `RxnAgg` et non `reactions`, jamais de
 * `Folder`, et un objet reçu n'a rien à faire dans « Mon espace » ni dans « Mes
 * fichiers ». Deux magasins, deux natures, aucune exception à écrire ensuite.
 */
export const edges = signal<readonly CachedEdge[]>([]);
export const memberships = signal<readonly CachedMembership[]>([]);

/** Les membres, **par groupe** : on ne les charge que pour le groupe regardé. */
export const members = signal<Readonly<Record<string, readonly Member[]>>>({});

/** Le fil du groupe regardé, et son curseur — `null` quand il est épuisé. */
export const feed = signal<readonly FeedItem[]>([]);
export const feedCursor = signal<string | null>(null);
export const feedBusy = signal<boolean>(false);

/**
 * **« Où va ma donnée »** (D15) — la fiche de partage de l'objet inspecté.
 * Chargée à la demande : la question ne se pose que quand on la pose.
 */
export const shares = signal<
  { fid: string; shares: readonly Share[]; excluded: readonly string[]; history: readonly ShareEvent[] } | undefined
>(undefined);

/** Les réacteurs nommés de l'objet inspecté — **au propriétaire seul**. */
export const reactors = signal<{ fid: string; items: readonly Reactor[] } | undefined>(undefined);

/** Une semaine : la fenêtre de ce qu'on appelle « nouveau » sur une pastille. */
const FRESH_MS = 7 * 24 * 3_600_000;

/**
 * Les arêtes d'un groupe, **les plus fraîches d'abord**.
 *
 * `EdgeUpdatedAt` et non `CreatedAt` : ce qui compte dans un fil est le moment où
 * la chose est **arrivée là**, pas celui où son auteur l'a prise en photo. Une
 * photo de l'an dernier partagée ce matin est une nouveauté de ce matin.
 */
export const edgesOfGroup = (gid: string): readonly CachedEdge[] =>
  edges.value
    .filter((e) => e.container === `GRP#${gid}`)
    .sort((a, b) => String(b['EdgeUpdatedAt'] ?? '').localeCompare(String(a['EdgeUpdatedAt'] ?? '')));

/**
 * Le compte de nouveautés d'un groupe — **une fenêtre, pas une marque de lecture**.
 *
 * Un « non lu » exigerait de mémoriser ce qui a été vu, par groupe et par appareil,
 * et de décider ce que « vu » veut dire — avoir ouvert l'onglet ? avoir défilé
 * jusqu'en bas ? Une fenêtre de sept jours ne prétend rien de tout cela : elle dit
 * *il s'est passé quelque chose récemment*, ce qui est exactement ce qu'une
 * pastille peut porter honnêtement.
 */
export const freshCount = (gid: string): number => freshOfGroup(gid).length;

/**
 * **Ce qui vient d'arriver dans un groupe, dans l'ordre où c'est arrivé.**
 *
 * La pastille disait *combien*, et rien ne disait *quoi* : il fallait ouvrir le
 * groupe, puis l'onglet *Nouveautés*, pour apprendre qu'il s'agissait de deux
 * photos qu'on avait déjà vues. Le retour du 2026-08-09 demande de le montrer
 * sur la ligne du groupe, en miniatures.
 *
 * **Aucune requête de plus, et c'est ce qui rend la bande possible.** Les arêtes
 * sont drainées par `/changes` et portent leur projection (D28) : nom, `Type`,
 * `Variants`, état. Une vignette se demande ensuite au lot de présignatures, qui
 * est déjà celui de la grille — une bande de six vignettes coûte donc *une*
 * entrée de lot, pas six appels.
 *
 * **Ce que la bande ne prétend pas dire.** Elle ne dit pas *dans quelle
 * collection* la chose est arrivée : le `Via` d'une arête — la seule donnée qui
 * le porte — est **retiré à la sérialisation**, parce qu'il décrit le graphe de
 * partage du propriétaire et non celui du lecteur. Grouper par collection
 * demanderait donc au serveur de le dire autrement, ce qui est un autre travail
 * ; une bande par groupe, elle, est exacte avec ce qu'on a.
 */
export const freshOfGroup = (gid: string): readonly Entity[] => {
  const since = Date.now() - FRESH_MS;
  return edgesOfGroup(gid)
    .filter((e) => {
      const at = Date.parse(String(e['EdgeUpdatedAt'] ?? ''));
      if (!Number.isFinite(at) || at < since) return false;
      // Un tombstone d'arête est déjà retiré du cache ; un **retrait** ne l'est
      // pas, et une révocation n'est pas une nouveauté.
      return e['Deleted'] !== true && e['Removed'] !== true;
    })
    .map((e) => e as unknown as Entity);
};

/**
 * L'**extrait** d'une note, tel qu'il se peint dans la grille. Il vient du
 * serveur (`Excerpt`) : le calculer ici demanderait de lire le contenu de chaque
 * vignette montée — une requête S3 par tuile, pour deux lignes de texte.
 */
export const excerptOf = (e: Entity): string | undefined =>
  typeof e.Excerpt === 'string' && e.Excerpt.length > 0 ? e.Excerpt : undefined;

/**
 * La liste blanche d'emoji, **servie par `GET /config`** et jamais codée ici.
 *
 * Le serveur refuse en `400` tout emoji hors liste : en proposer un de plus dans
 * l'interface produirait un refus après le clic, sans que l'utilisateur puisse
 * comprendre lequel de ses gestes était interdit. Le repli est vide — mieux vaut
 * aucune palette qu'une palette fausse.
 */
export const emojiPalette = signal<readonly string[]>([]);

/**
 * **La sélection multiple.** Distincte de `selection`, qui est le `fid` regardé par
 * l'inspecteur : on inspecte une entité, on en agit sur plusieurs. Les confondre
 * ferait basculer l'inspecteur à chaque case cochée.
 */
export const picked = signal<ReadonlySet<string>>(new Set());

export function togglePicked(fid: string): void {
  const next = new Set(picked.value);
  if (!next.delete(fid)) next.add(fid);
  picked.value = next;
}

export const clearPicked = (): void => void (picked.value = new Set());

/**
 * **Le sélecteur de collection**, ouvert sur des `fid` explicites.
 *
 * Il porte sa propre liste plutôt que de lire `picked` : le même geste doit
 * partir d'une tuile, d'une ligne d'arborescence ou d'une sélection multiple, et
 * le faire dépendre de la sélection l'aurait à nouveau enfermé dans l'écran qui
 * possède la sélection — le défaut même qu'on corrige.
 */
export const picker = signal<readonly string[] | undefined>(undefined);

export const openPicker = (fids: readonly string[]): void => void (picker.value = fids);
export const closePicker = (): void => void (picker.value = undefined);

/** La date d'une entité **telle qu'elle s'affiche** : jamais `UpdatedAt` (E7). */
export const dateOf = (e: Entity): string => String(e.DocumentDate ?? e.CreatedAt);

/**
 * La note moyenne — **calculée par le serveur à la réponse**, jamais stockée.
 *
 * Elle arrive sous `reactions.scoreAvg`, en `lowerCamel` : c'est une *vue*, pas un
 * attribut de l'item (api.md §0). La map nominative `Reactions` **ne sort d'aucune
 * route** — elle nommerait des réacteurs d'autres groupes —, et un client qui la
 * lirait ne trouverait jamais rien. C'est le défaut qu'a révélé le premier
 * parcours de 4.4 : la pastille de note ne s'affichait pas, et rien d'autre ne
 * l'aurait montré.
 */
export const scoreOf = (e: Entity): number | undefined => e.reactions?.scoreAvg ?? undefined;

/** Les emoji posés, **dédoublonnés** : l'agrégat est un décompte, pas une liste. */
export const emojisOf = (e: Entity): readonly string[] =>
  Object.keys(e.reactions?.emoji ?? {});

/**
 * **Exporté pour être mesuré** *(R3)* : les deux critères neufs se trompent en
 * silence — un filtre qui rend zéro se lit comme « je n'ai rien de tel », jamais
 * comme « le test est faux ». C'est la leçon de R1, transposée d'une borne à un
 * prédicat.
 */
export const matches = (e: Entity, f: GalleryFilter): boolean => {
  if (f.type && e.Type !== f.type) return false;
  if (f.kind && e.Kind !== f.kind) return false;
  if (f.camera && e.Camera !== f.camera) return false;
  if (f.emoji && !emojisOf(e).includes(f.emoji)) return false;
  // **`null` est un critère, pas une absence** : `f.album === undefined` veut dire
  // « on n'a rien demandé », `null` veut dire « ce qui ne circule pas ». Un
  // `if (f.album)` aurait avalé le second, et sans rien casser d'apparent.
  if (f.album !== undefined) {
    const inside = e.albums ?? [];
    if (f.album === null ? inside.length > 0 : !inside.includes(f.album)) return false;
  }
  // `Owner` est **public et toujours servi** — `PUBLIC_FIELDS` côté entité,
  // `PROJECTED` côté arête —, donc le critère n'a pas de repli à écrire ici.
  if (f.owner !== undefined && e.Owner !== f.owner) return false;
  if (f.minScore !== undefined) {
    const avg = scoreOf(e);
    // Une entité **sans avis** est écartée : « au moins 3 » ne peut pas inclure
    // « personne n'a noté ». Traiter l'absence comme un zéro serait une réponse
    // là où il y a un silence.
    if (avg === undefined || avg < f.minScore) return false;
  }
  return true;
};

const compare = (sort: GallerySort) => (a: Entity, b: Entity) => {
  switch (sort) {
    // L'ordre d'un dossier : les mêmes natures ensemble, puis l'alphabet.
    case 'kind':
      return `${a.Type}${a.Name}`.localeCompare(`${b.Type}${b.Name}`);
    case 'date-asc':
      return dateOf(a).localeCompare(dateOf(b));
    case 'name':
      return a.Name.localeCompare(b.Name);
    case 'size-desc':
      return (b.Variants?.['source']?.size ?? 0) - (a.Variants?.['source']?.size ?? 0);
    case 'date-desc':
      return dateOf(b).localeCompare(dateOf(a));
  }
};

/**
 * La liste affichée. Les axes trient différemment parce qu'ils répondent à des
 * questions différentes : un dossier se lit par ordre alphabétique, une
 * photothèque par date de prise de vue, un album par ce qu'on y a mis.
 */
export const items = computed<readonly Entity[]>(() => {
  // **La recherche passe après les filtres et le tri** *(R7)* : elle ne retient que
  // ce qui trouve un mot, et, à pertinence égale, garde l'ordre de l'écran.
  const listed = listedItems.value;
  return searching.value ? search(listed, searchQuery.value, albumNames.value) : listed;
});

/** Le mode doublons est-il actif **là où l'on regarde** — son espace, ou un partage. */
export const duplicatesMode = computed<boolean>(
  () => applicableFilter(galleryFilter.value, view.value.kind).duplicates === true,
);

/**
 * **Les groupes de doublons de son espace** *(K4)* — `fid` → identifiant de groupe,
 * pour chaque photo qui a au moins un voisin au seuil réglé. Calculés sur le miroir,
 * donc sur ce qui est synchronisé, comme tous les filtres — et **seulement en mode
 * doublons** : hors de lui, la comparaison de toutes les paires ne tourne pas, et
 * aucun badge ne se peint.
 */
const NO_GROUPS: ReadonlyMap<string, string> = new Map();
export const duplicates = computed<ReadonlyMap<string, string>>(() => {
  if (!duplicatesMode.value) return NO_GROUPS;
  // **Dans un partage, la source n'est pas le miroir** *(DB12)* : son contenu
  // vient d'arêtes, et l'empreinte y est servie au point de lecture sous
  // `visualHash`. Le `Sha256`, lui, n'y est **jamais** — il reste privé —, donc un
  // partage ne fusionne que par ressemblance.
  const v = view.value;
  const source: readonly Fingerprinted[] =
    v.kind === 'collection'
      ? albumItems.value.map((e) => ({ Fid: e.Fid, VisualHash: e.visualHash }))
      : entities.value;
  return duplicateGroups(source, duplicateThreshold.value);
});

/** Combien de photos compte le groupe de chaque `fid` — ce que dit le badge. */
export const duplicateCounts = computed<ReadonlyMap<string, number>>(() => {
  const size = new Map<string, number>();
  for (const g of duplicates.value.values()) size.set(g, (size.get(g) ?? 0) + 1);
  const out = new Map<string, number>();
  for (const [fid, g] of duplicates.value) out.set(fid, size.get(g)!);
  return out;
});

/**
 * **Le mode doublons**, appliqué après les autres filtres dans son propre espace : ne
 * garder que les photos d'un groupe, et **les regrouper** — un groupe éparpillé par
 * le tri de l'écran ne se lirait plus comme un groupe.
 */
function ordered(list: Entity[], f: GalleryFilter): Entity[] {
  if (!f.duplicates) return list;
  const groups = duplicates.value;
  return sortByDuplicates(
    list.filter((e) => e.Fid !== undefined && groups.has(e.Fid)),
    groups,
    dateOf,
  );
}

/** La liste de l'écran **avant recherche** — filtrée et triée. */
const listedItems = computed<readonly Entity[]>(() => {
  const v = view.value;
  // **Le filtre passe par `applicableFilter` avant d'être appliqué** *(R3)* : un
  // critère posé dans un dossier ne doit pas vider un partage qu'on ouvre ensuite.
  const f = applicableFilter(galleryFilter.value, v.kind);
  if (v.kind === 'folder') {
    // Les collections n'apparaissent pas : elles n'ont plus de `Folder`, donc ce
    // test les écarte **sans avoir à les nommer**. Une exception écrite ici
    // aurait été une exception de plus à tenir à jour.
    //
    // **Les filtres s'y appliquent aussi** depuis que les deux axes vivent dans
    // la même destination : « mes vidéos notées 4 » est la même question posée
    // dans un dossier, et la réserver à un seul des deux écrans revenait à faire
    // dépendre du lieu ce qui ne dépend que de l'objet.
    return ordered(
      [...entities.value]
        .filter((e) => e.Folder === v.folder && matches(e, f))
        .sort(compare(gallerySort.value)),
      f,
    );
  }
  if (v.kind === 'files') {
    // **Tous les types**, plus seulement les médias : l'axe *Tout* répond à
    // *qu'est-ce que j'ai ?*, et une note en fait partie. Seules les collections
    // en sont exclues — une collection n'est pas un fichier.
    return ordered(
      [...entities.value]
        .filter((e) => e.Aid === undefined && matches(e, f))
        .sort(compare(gallerySort.value)),
      f,
    );
  }
  if (v.kind === 'collection') {
    // **Les filtres valent ici aussi** *(2026-08-14)*. Ils ne s'appliquaient
    // qu'aux deux axes de *Mon espace*, si bien qu'ouvrir une collection les
    // faisait disparaître de l'écran — et depuis que l'écran les affiche, les
    // laisser inertes serait pire : des contrôles qui ne font rien.
    //
    // `ordered` y est passé **depuis DB12** : c'est lui qui ne garde que les
    // groupes et les rassemble, en mode doublons.
    return ordered(
      [...albumItems.value].filter((e) => matches(e, f)).sort(compare(gallerySort.value)),
      f,
    );
  }
  return [];
});

/**
 * **Comment se peint un dossier** — en lignes, ou en vignettes.
 *
 * Le dossier a longtemps eu une seule réponse : des lignes de texte, avec une
 * icône générique de 36 px pour toute image. Sur un produit dont le sujet *est*
 * l'image, c'est le défaut le plus visible qui soit — deux photos à l'écran, et
 * pas un pixel de photo.
 *
 * `auto` est le défaut, et il **suit le contenu** : un dossier qui porte au moins
 * une chose vignettable se peint en grille, un dossier de notes reste en lignes.
 * La règle est celle-là et non « ce dossier est-il dans Photos » : un dossier
 * n'a pas de type, seul son contenu en a un.
 *
 * Le choix explicite, lui, est **une préférence d'appareil** et se garde : qui
 * préfère les lignes ne veut pas le redire à chaque dossier.
 *
 * **Il vaut des deux côtés depuis le 2026-08-09.** Il ne valait que sur l'axe
 * *Dossiers* — l'axe *Tout* était une grille et rien d'autre —, si bien que
 * « montre-moi ça en liste » dépendait de l'axe où on le demandait. C'est le même
 * défaut que celui des filtres, corrigé le même jour et pour la même raison : une
 * préférence d'affichage porte sur des objets, pas sur un écran.
 */
export type SpaceLayout = 'auto' | 'list' | 'grid';

const LAYOUT_KEY = 'synkara.layout';

export const spaceLayout = signal<SpaceLayout>(
  ((): SpaceLayout => {
    const raw = localStorage.getItem(LAYOUT_KEY);
    return raw === 'list' || raw === 'grid' ? raw : 'auto';
  })(),
);

export function setSpaceLayout(next: SpaceLayout): void {
  spaceLayout.value = next;
  localStorage.setItem(LAYOUT_KEY, next);
}

/**
 * Le drapeau **résolu** — la vue ne connaît que celui-là.
 *
 * Le test est `Variants.thumb !== undefined`, c'est-à-dire *ce type déclare-t-il
 * une vignette*, et non *l'a-t-il déjà*. Une photo en cours de traitement doit
 * mettre le dossier en grille, sans quoi il basculerait de lignes en vignettes
 * quelques secondes après l'envoi, sous les doigts.
 */
export const spaceIsGrid = computed<boolean>(() => {
  if (spaceLayout.value !== 'auto') return spaceLayout.value === 'grid';
  return items.value.some((e) => e.Variants?.['thumb'] !== undefined);
});

/**
 * Les appareils photo présents dans le miroir — le filtre ne propose **que ce qui
 * existe**. Une liste fermée d'appareils connus n'aurait aucun sens : `Camera` est
 * ce que l'EXIF a écrit, et personne ne l'a normalisé.
 */
export const cameras = computed<readonly string[]>(() =>
  [
    ...new Set(
      entities.value
        .filter((e) => e.Type === 'MEDIA' && e.Camera)
        .map((e) => String(e.Camera)),
    ),
  ].sort((a, b) => a.localeCompare(b)),
);

/** Un choix de filtre : ce qu'on affiche, et ce qu'on pose. */
export interface FilterChoice {
  readonly value: string;
  readonly label: string;
}

/**
 * **Les partages présents dans ce qu'on regarde** — et non tous ceux qu'on possède
 * *(R3, 2026-09-12)*.
 *
 * La règle est celle de `cameras` — « seulement ce qui existe » — mais son
 * périmètre est plus étroit, et à dessein : la question posée dans un dossier est
 * *ce qui, **ici**, circule*. Offrir les trente partages du compte, dont deux
 * touchent ce dossier, c'est offrir vingt-huit réponses vides d'avance — le
 * travers que `contributableAlbums` corrige déjà dans le sélecteur.
 *
 * Le périmètre est celui de l'axe **avant filtres**, comme le total affiché : une
 * liste qui rétrécit au fur et à mesure qu'on s'en sert empêcherait de revenir sur
 * son choix.
 *
 * Un `aid` **inconnu de la liste des partages** est ignoré plutôt que rendu tel
 * quel : ce serait un ULID déguisé en nom, la même faute que celle dont
 * `profiles.ts` se garde côté serveur. Il n'en reste que deux cas — la liste pas
 * encore chargée, et un partage supprimé dont l'entité porte encore l'appartenance
 * — et ni l'un ni l'autre n'a de nom à afficher.
 */
export function albumsInScope(
  scope: readonly Entity[],
  known: readonly Entity[] | undefined,
): readonly FilterChoice[] {
  const present = new Set(scope.flatMap((e) => [...(e.albums ?? [])]));
  return (known ?? [])
    .filter((a) => a.Aid !== undefined && present.has(a.Aid))
    .map((a) => ({ value: String(a.Aid), label: a.Name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * **Qui a versé dans ce partage** — les propriétaires distincts de la page ouverte
 * *(R3, 2026-09-12)*.
 *
 * `moi` est passé à part et nommé à part : un partage collectif où l'on cherche
 * « mes éléments » ne peut pas obliger à reconnaître son propre alias dans une
 * liste. Les autres sont nommés par `ownerAlias`, que la route hydrate depuis R3 ;
 * sans alias, `anonyme` — **jamais** le `uid`.
 */
export function ownersInScope(
  scope: readonly AlbumItem[],
  me: string | undefined,
  labels: { readonly mine: string; readonly anonymous: string },
): readonly FilterChoice[] {
  const named = new Map<string, string>();
  for (const e of scope) {
    const uid = e.Owner;
    if (!uid) continue;
    if (uid === me) {
      named.set(uid, labels.mine);
      continue;
    }
    // Le premier alias non vide gagne : une page peut porter la même personne sur
    // vingt lignes, et une seule d'entre elles suffit à la nommer.
    if (!named.has(uid) || named.get(uid) === labels.anonymous)
      named.set(uid, e.ownerAlias ?? labels.anonymous);
  }
  return [...named]
    .map(([value, label]) => ({ value, label }))
    // **Soi d'abord**, puis l'alphabet : c'est l'entrée qu'on vient chercher.
    .sort((a, b) =>
      a.value === me ? -1 : b.value === me ? 1 : a.label.localeCompare(b.label),
    );
}

/**
 * La grille **groupée par mois** — la question de la photothèque est *quand*.
 *
 * Le groupement n'a de sens que sur un tri par date : trié par nom, un en-tête de
 * mois réapparaîtrait toutes les trois tuiles et ne dirait plus rien. C'est cette
 * règle-là, et non la destination, qui décide : un dossier trié par défaut — type
 * puis nom — rend **un seul groupe sans en-tête**, et la grille virtualisée sait
 * déjà taire l'en-tête d'un groupe dont la clé est vide. Il n'y a donc pas de
 * seconde projection à tenir pour les dossiers ; il y en avait une, `flatGroup`,
 * et deux projections finissent par diverger.
 */
export interface MonthGroup {
  readonly key: string;
  readonly items: readonly Entity[];
}

export const months = computed<readonly MonthGroup[]>(() => {
  // Sous recherche, l'ordre est la **pertinence** : un en-tête de mois couperait la
  // meilleure réponse de la deuxième dès qu'elles n'ont pas le même mois.
  // **En mode doublons, l'ordre est celui des groupes** : un en-tête de mois couperait
  // un groupe dont les copies n'ont pas la même date.
  if (
    searching.value ||
    duplicatesMode.value ||
    (gallerySort.value !== 'date-desc' && gallerySort.value !== 'date-asc')
  ) {
    return items.value.length > 0 ? [{ key: '', items: items.value }] : [];
  }
  const groups: MonthGroup[] = [];
  let current: { key: string; items: Entity[] } | undefined;
  for (const e of items.value) {
    const key = dateOf(e).slice(0, 7);
    if (!current || current.key !== key) {
      current = { key, items: [] };
      groups.push(current);
    }
    current.items.push(e);
  }
  return groups;
});

/** Fusionne des entités fraîches dans le miroir, par identifiant. */
export function mergeEntities(fresh: readonly Entity[]): void {
  const byId = new Map(entities.value.map((e) => [idOf(e), e]));
  for (const e of fresh) byId.set(idOf(e), e);
  entities.value = [...byId.values()];
}

export function forgetEntity(id: string): void {
  entities.value = entities.value.filter((e) => idOf(e) !== id);
}

/**
 * **L'inspecteur** — le `fid` regardé, et la fiche complète quand elle est
 * arrivée. Deux signaux et non un : la sélection est instantanée, la fiche
 * demande un aller-retour (`GET /files/{fid}`), et confondre les deux ferait
 * clignoter le panneau entre deux clics.
 */
export const selection = signal<string | undefined>(undefined);
export const detail = signal<Entity | undefined>(undefined);
export const detailBusy = signal<boolean>(false);

/**
 * La visionneuse : **un index dans une liste figée à l'ouverture**.
 *
 * Figée, parce qu'une liste qui bouge sous les pieds — une synchronisation qui
 * ramène une photo, une suppression ailleurs — ferait sauter d'une image à
 * l'autre sans que personne ait touché une flèche.
 */
export const viewer = signal<number | undefined>(undefined);
export const viewerList = signal<readonly Entity[]>([]);

export function openViewer(list: readonly Entity[], index: number): void {
  viewerList.value = list;
  viewer.value = index;
}

/**
 * **Le lecteur** — une note ou un document ouvert.
 *
 * Distinct de la visionneuse parce qu'il ne se parcourt pas : on ouvre *un*
 * document, on ne feuillette pas une pile de PDF. Confondre les deux aurait
 * donné des flèches qui ne mènent nulle part.
 */
export const reader = signal<Entity | undefined>(undefined);

/**
 * **L'éditeur de notes** (4.3).
 *
 * `text` est l'état de frappe ; `baseRev` la révision sur laquelle il repose, et
 * **il n'est pas dérivable de l'entité** : après un enregistrement réussi, la
 * base avance alors que le miroir peut encore porter l'ancienne. Les confondre
 * ferait échouer le second enregistrement d'une même session en `stale_rev`.
 *
 * `saved` est le texte tel qu'il est enregistré : c'est **sa comparaison avec
 * `text`** qui définit « modifié », et non un drapeau qu'on lèverait à la frappe.
 * Un drapeau resterait levé après une annulation qui ramène au texte d'origine,
 * et l'éditeur enregistrerait pour rien — donc une révision de plus (D20).
 */
export interface EditorState {
  readonly entity: Entity;
  readonly text: string;
  readonly saved: string;
  readonly baseRev: number;
  /**
   * **En clair, ou en Markdown.**
   *
   * `rich` est le défaut depuis le 2026-08-09 : l'app écrit ses notes dans un
   * éditeur de blocs depuis 4.2 bis, et le site n'offrait qu'une zone de texte
   * brut — la même note s'écrivait donc en `#` d'un côté et en titre de l'autre.
   *
   * `source` reste, et n'est pas un aperçu de repli : c'est la vue de ce qui est
   * réellement enregistré, la seule qui montre une syntaxe que l'éditeur en clair
   * ne saurait pas produire — et le seul recours quand un aller-retour a
   * normalisé quelque chose qu'on ne voulait pas voir changer.
   */
  readonly mode: 'rich' | 'source';
  readonly saving: boolean;
  /**
   * **Combien de fois le texte a changé pour une raison extérieure au clavier** :
   * ouverture, relecture après conflit, restauration, retour depuis la source.
   *
   * L'éditeur en clair est un `contenteditable` — repeindre son document à
   * chaque frappe détruirait le curseur. Ce compteur est donc le **seul** signal
   * qui l'autorise à se repeindre, et il ne bouge jamais sous les doigts.
   */
  readonly loaded: number;
  readonly conflict?: ConflictState;
  /**
   * **La porte de sortie** *(2026-08-16)*.
   *
   * Fermer enregistre d'abord et ne ferme que si c'est passé — ce qui est juste
   * tant que l'enregistrement *peut* passer. Sur une note qu'on n'a pas le droit
   * d'écrire, il ne le peut jamais : la croix, `Échap` et le geste de retour
   * repartaient tous par le même chemin, et l'éditeur devenait une pièce sans
   * porte. Le drapeau marque « une sortie a déjà échoué » ; la suivante est
   * franche, et l'écran le dit avant de la proposer.
   */
  readonly leaving?: boolean;
  /** `undefined` tant que l'historique n'a pas été demandé ; `[]` s'il est vide. */
  readonly history?: readonly Revision[];
}

/** Le conflit tel qu'il s'affiche. Quatre situations, quatre issues. */
export type ConflictState =
  | { readonly kind: 'stale'; readonly currentRev: number; readonly remote: string }
  | { readonly kind: 'locked'; readonly expiresAt: string | null }
  | { readonly kind: 'lockLost'; readonly currentRev: number }
  | { readonly kind: 'missingBytes' };

export const editor = signal<EditorState | undefined>(undefined);

/** Modifie l'état de l'éditeur sans le recréer à la main à chaque appel. */
export function patchEditor(patch: Partial<EditorState>): void {
  const current = editor.value;
  if (!current) return;
  editor.value = { ...current, ...patch };
}

/** **Modifié** se déduit, il ne se déclare pas. */
export const editorDirty = computed<boolean>(() => {
  const e = editor.value;
  return e !== undefined && e.text !== e.saved;
});

export function closeViewer(): void {
  viewer.value = undefined;
  viewerList.value = [];
}

/**
 * ## L'arborescence — **une vue, dérivée de chemins plats**
 *
 * `GET /folders` rend une liste : il n'existe aucun item « parent », l'attribut
 * `Folder` de l'entité est seul autoritatif. La hiérarchie se reconstruit donc
 * ici, et c'est pourquoi `/a/b` peut exister sans `/a` — le calcul rattache alors
 * `/a/b` au plus proche ancêtre **qui existe**, la racine, au lieu d'inventer un
 * nœud que le serveur ne connaît pas.
 *
 * Jusqu'à 4.5 bis, l'arbre était une liste indentée : aucun symbole, aucun pli.
 * Passé une dizaine de dossiers, il devenait illisible — et rien ne permettait de
 * refermer une branche. Le pli est donc une propriété du **nœud parent**, pas une
 * profondeur maximale globale : replier `/Photos` doit cacher `/Photos/2026` sans
 * rien dire de `/Documents`.
 */
export interface TreeNode {
  readonly folder: string;
  readonly label: string;
  readonly depth: number;
  /** Ce que le serveur compte : les objets **rangés là**, sans les descendants. */
  readonly itemCount: number;
  /**
   * **Ce que la branche pèse** — le dossier et tout ce qui est dessous.
   *
   * Le compte du serveur est celui d'un dossier seul : un `/Photos` qui range
   * tout par année s'affichait « 0 » avec quatre mille photos dessous, ce qui
   * fait chercher un bogue. Le total est **calculé ici, sur la liste déjà en
   * main** — `GET /folders` rend l'arborescence entière en une réponse (plafond
   * de 500 dossiers) : il n'y a donc **aucun appel de plus**, et surtout aucun
   * appel *par dossier*, qui serait la façon la plus chère de répondre à une
   * question dont on a déjà toutes les données.
   */
  readonly totalCount: number;
  readonly parent?: string;
  readonly hasChildren: boolean;
}

/**
 * Le poids de chaque branche, en **un passage sur les ancêtres**.
 *
 * On remonte le chemin de chaque dossier plutôt que de chercher, pour chaque
 * dossier, ceux qui commencent par lui : la profondeur est bornée à 10 par le
 * serveur, là où le second calcul serait quadratique. Un ancêtre absent de la
 * liste est **sauté** et non inventé — `/a/b` peut exister sans `/a`, et
 * fabriquer un nœud que le serveur ne connaît pas ferait diverger l'arbre.
 */
function totalCounts(list: readonly Folder[]): ReadonlyMap<string, number> {
  const totals = new Map(list.map((f) => [f.folder, f.itemCount]));
  for (const f of list) {
    if (f.folder === ROOT) continue;
    for (let cut = f.folder.lastIndexOf('/'); cut >= 0; cut = f.folder.lastIndexOf('/', cut - 1)) {
      const ancestor = cut === 0 ? ROOT : f.folder.slice(0, cut);
      const known = totals.get(ancestor);
      if (known !== undefined) totals.set(ancestor, known + f.itemCount);
      if (cut === 0) break;
    }
  }
  return totals;
}

const isUnder = (child: string, parent: string): boolean =>
  parent === ROOT ? child !== ROOT : child.startsWith(`${parent}/`);

/**
 * Le nom affiché d'un dossier. La racine porte un libellé traduit ; les autres
 * ne montrent que leur **dernier segment** — le chemin entier reste dans
 * l'info-bulle, où il sert à lever une ambiguïté sans coûter une ligne.
 */
const labelOf = (folder: string, rootLabel: string): string =>
  folder === ROOT ? rootLabel : folder.slice(folder.lastIndexOf('/') + 1);

/**
 * Aplatit les chemins en nœuds, **dans l'ordre d'affichage**.
 *
 * Le tri lexicographique suffit à garantir qu'un parent précède ses enfants, ce
 * qui permet de tenir la filiation avec une simple pile plutôt qu'en cherchant,
 * pour chaque dossier, son plus long préfixe existant.
 */
export function treeNodes(list: readonly Folder[], rootLabel: string): readonly TreeNode[] {
  const sorted = [...list].sort((a, b) => a.folder.localeCompare(b.folder));
  const totals = totalCounts(list);
  const stack: string[] = [];
  const nodes: {
    folder: string;
    label: string;
    depth: number;
    itemCount: number;
    totalCount: number;
    parent?: string;
  }[] = [];

  for (const f of sorted) {
    while (stack.length > 0 && !isUnder(f.folder, stack[stack.length - 1]!)) stack.pop();
    nodes.push({
      folder: f.folder,
      label: labelOf(f.folder, rootLabel),
      depth: stack.length,
      itemCount: f.itemCount,
      totalCount: totals.get(f.folder) ?? f.itemCount,
      ...(stack.length > 0 ? { parent: stack[stack.length - 1]! } : {}),
    });
    stack.push(f.folder);
  }

  // Un nœud a des enfants si le **suivant** est plus profond : la liste est déjà
  // dans l'ordre de l'arbre, il n'y a donc rien à chercher.
  return nodes.map((n, i) => ({ ...n, hasChildren: (nodes[i + 1]?.depth ?? 0) > n.depth }));
}

/**
 * **Les branches ouvertes.** La racine l'est d'emblée ; le reste se replie.
 *
 * Ce n'est pas persisté entre deux sessions, et c'est délibéré : un pli est une
 * intention du moment — « je regarde ailleurs » —, pas une préférence. Le
 * retrouver trois jours plus tard cacherait un dossier sans qu'on se souvienne de
 * l'avoir demandé.
 */
export const expanded = signal<ReadonlySet<string>>(new Set([ROOT]));

export function toggleExpanded(folder: string): void {
  const next = new Set(expanded.value);
  if (!next.delete(folder)) next.add(folder);
  expanded.value = next;
}

/**
 * Ouvre le chemin qui mène à un dossier. Naviguer vers `/a/b/c` en laissant `/a`
 * replié ferait disparaître de l'arbre le dossier qu'on vient d'ouvrir.
 */
export function revealFolder(folder: string): void {
  const next = new Set(expanded.value);
  next.add(ROOT);
  for (let i = folder.indexOf('/', 1); i > 0; i = folder.indexOf('/', i + 1)) {
    next.add(folder.slice(0, i));
  }
  next.add(folder);
  expanded.value = next;
}

/** Ce qui se voit : un nœud dont **tous** les ancêtres sont dépliés. */
export function visibleNodes(nodes: readonly TreeNode[], open: ReadonlySet<string>): readonly TreeNode[] {
  const shown = new Set<string>();
  const out: TreeNode[] = [];
  for (const node of nodes) {
    if (node.parent !== undefined && !(shown.has(node.parent) && open.has(node.parent))) continue;
    shown.add(node.folder);
    out.push(node);
  }
  return out;
}

/**
 * Le dossier de destination d'une création : celui qu'on regarde, ou **rien**.
 *
 * `undefined` et non `ROOT` : hors de l'arborescence, le client n'a pas d'avis, et
 * c'est au serveur de poser le défaut du type. Rendre la racine reviendrait à
 * dire « range ça à la racine », ce qui n'est pas ce que veut quelqu'un qui
 * envoie une photo depuis « Mes fichiers ».
 */
export const currentFolder = (): string | undefined =>
  view.value.kind === 'folder' ? view.value.folder : undefined;
